// bizSAFE — the WSHC client-portal process (from 2 Nov 2026 WSHC's new portal: companies apply themselves with
// CorpPass; CCIS no longer submits for them). ONE place for its rules, so the job page, Jobs list and Dashboard
// never disagree. A job is on the new process only when jobs.wshc_flow = 'client_portal' (set when its report is
// first sent while the switch is on); every other job keeps the earlier process and its screens unchanged.
//
// displayState() in workflow.js keeps its 7 values (counts, filters, colours and owners depend on them); the
// wording for new-process jobs comes from portalStepText() here instead.
import { subMonths, parseISO, format, differenceInCalendarDays } from 'date-fns'
import { AUDIT_TYPES } from './supabase'

export const isPortalJob = (job) => !!job && job.wshc_flow === 'client_portal' && job.audit_type === AUDIT_TYPES.BIZSAFE

// WSHC accepts a RENEWAL application from 2 months before the current expiry. One rule for every screen
// (the job page lock, the Dashboard and the daily emails all use this same calendar-month sum).
export function wshcWindowOpensOn(expiry) {
  if (!expiry) return null
  try { return format(subMonths(parseISO(String(expiry).slice(0, 10)), 2), 'yyyy-MM-dd') } catch (e) { return null }
}
export const todaySg = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' })
export function wshcWindowOpen(expiry) {
  const opens = wshcWindowOpensOn(expiry)
  return !opens || todaySg() >= opens   // no expiry = new applicant = never locked
}
export const fmtDay = (d) => { try { return d ? format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy') : '' } catch (e) { return '' } }

export const signedCurrent = (job) => !!job?.report_acknowledged_at && job.report_acknowledged_version === job.report_ack_version
// Ended without a certificate (a later Finalise sets wshc_outcome 'Accepted', so the job then reads as finalised).
export const ENDED_OUTCOME = 'Ended – no certificate'
export const isEnded = (job) => job?.status === 'Completed' && job.wshc_outcome === ENDED_OUTCOME

// Days since the "please apply" email (calendar days).
export function daysSinceApplyEmail(job) {
  if (!job?.wshc_apply_email_sent_at) return null
  return differenceInCalendarDays(new Date(), new Date(job.wshc_apply_email_sent_at))
}

// "End job — no new certificate" is offered after the apply email, or — if the client never signed — once the
// sign-off reminders have finished (14 days after the report was sent). Mirrors end_bizsafe_job on the server.
export function canEndJob(job) {
  if (!isPortalJob(job) || ['Completed', 'Cancelled'].includes(job.status)) return false
  if (job.wshc_apply_email_sent_at) return true
  return !!job.report_ack_sent_at && !signedCurrent(job)
    && Date.now() - new Date(job.report_ack_sent_at).getTime() >= 14 * 24 * 3600 * 1000   // same rule as the server
}

// ONE label set for both processes (owner-approved 2 Nov 2026). New-process jobs differ in only two places:
//   'Client Acknowledged' instead of 'Ready for WSHC Submission' (the office must NOT submit on WSHC), and
//   'Ended – no certificate'. Everything else uses the usual labels, incl. 'Pending WSHC Outcome' from the moment
//   the apply email goes out — so the office's weekly WSHC check is ONE filter for old and new jobs.
export const CLIENT_ACKNOWLEDGED = 'Client Acknowledged'
export const PENDING_WSHC = 'Pending WSHC Outcome'

// Label for a new-process job (status badge / heading / Jobs filter). null = use the usual wording.
export function portalStepText(job) {
  if (!isPortalJob(job)) return null
  if (job.status === 'Cancelled') return null
  if (isEnded(job)) return ENDED_OUTCOME
  if (job.status === 'Completed') return null
  if (job.workflow_state === 'Pending WSHC Outcome') return PENDING_WSHC
  if (job.office_review_at && signedCurrent(job)) return CLIENT_ACKNOWLEDGED
  return null
}
export const portalListLabel = (job) => portalStepText(job)

// Next step for the sticky bar on the job page (new-process jobs only; null = use nextActionOf()).
// expiry = the client's current bizSAFE expiry (null for a new applicant).
export function portalNextAction(job, expiry) {
  const t = portalStepText(job)
  if (!t || t === ENDED_OUTCOME) return null
  if (t === CLIENT_ACKNOWLEDGED) {
    if (!job.wshc_applicant_email) return 'Add the client’s assigned staff (who applies on WSHC)'
    if (!wshcWindowOpen(expiry)) return `Nothing to do — AuditPM emails the client the WSHC steps on ${fmtDay(wshcWindowOpensOn(expiry))}`
    return 'Nothing to do — AuditPM emails the client the WSHC steps as soon as the signed report is ready'
  }
  if (job.client_wshc_submitted_at) return `Client says applied ${fmtDay(job.client_wshc_submitted_at)} — check WSHC in your weekly check, then Finalise`
  const days = daysSinceApplyEmail(job)
  if (days != null && days >= 28) return 'No reply after 28 days — call the client'
  return `Steps emailed ${fmtDay(job.wshc_apply_email_sent_at)} — check WSHC in your weekly check, then Finalise`
}

// Badge colours (same palette as workflow.js stateChip; amber when the office must add the assigned staff).
export function portalChip(text, job) {
  if (!text) return null
  if (text === ENDED_OUTCOME) return { bg: '#f0f0f0', color: '#555' }
  if (text === CLIENT_ACKNOWLEDGED) return job && !job.wshc_applicant_email ? { bg: '#FFF3E0', color: '#854F0B' } : { bg: '#EDE7F6', color: '#5E35B1' }
  return { bg: '#E3F2FD', color: '#1565C0' }
}
